import { useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import L from "leaflet";
import "leaflet.markercluster";
import "leaflet.markercluster/dist/MarkerCluster.css";
import "leaflet.markercluster/dist/MarkerCluster.Default.css";
import { euro } from "@/lib/format";

export const PropertiesMap = ({ points = [], center = [45.4375, 13.523], zoom = 13, height = "520px", onlyOne = false }) => {
  const ref = useRef(null);
  const mapRef = useRef(null);
  const navigate = useNavigate();

  useEffect(() => {
    if (!ref.current || mapRef.current) return;
    const map = L.map(ref.current, { scrollWheelZoom: false, attributionControl: true }).setView(center, zoom);
    mapRef.current = map;
    L.tileLayer("https://{s}.basemaps.cartocdn.com/rastertiles/voyager/{z}/{x}/{y}{r}.png", {
      attribution: "&copy; OpenStreetMap &copy; CARTO",
      maxZoom: 19,
    }).addTo(map);
    return () => {
      map.remove();
      mapRef.current = null;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const map = mapRef.current;
    if (!map || !points.length) return;

    if (onlyOne) {
      const p = points[0];
      const icon = L.divIcon({
        className: "",
        html: `<div class="price-pin active" data-testid="property-map-pin">${p.name}</div>`,
        iconSize: [60, 26],
        iconAnchor: [30, 26],
      });
      const m = L.marker([p.lat, p.lng], { icon }).addTo(map);
      return () => map.removeLayer(m);
    }

    const cluster = L.markerClusterGroup({
      showCoverageOnHover: false,
      maxClusterRadius: 44,
      spiderfyDistanceMultiplier: 1.6,
      iconCreateFunction: (c) =>
        L.divIcon({
          className: "",
          html: `<div class="cluster-pin">${c.getChildCount()}</div>`,
          iconSize: [40, 40],
        }),
    });
    points.forEach((p) => {
      const icon = L.divIcon({
        className: "",
        html: `<div class="price-pin" data-testid="property-map-pin">${euro(p.best_price)}</div>`,
        iconSize: [60, 26],
        iconAnchor: [30, 26],
      });
      const m = L.marker([p.lat, p.lng], { icon });
      m.bindTooltip(`${p.name} · ${euro(p.best_price)}`, { direction: "top", offset: [0, -20] });
      m.on("click", () => navigate(`/stay/${p.slug}`));
      cluster.addLayer(m);
    });
    map.addLayer(cluster);
    if (points.length > 1) map.fitBounds(cluster.getBounds().pad(0.2));
    return () => map.removeLayer(cluster);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [points]);

  return (
    <div
      ref={ref}
      data-testid="properties-map-view"
      style={{ height }}
      className="w-full overflow-hidden rounded-2xl border border-sand-border z-0"
    />
  );
};
